/**
 * UserContext.jsx
 * Provides the logged-in user's email and TMG profile (role + sites) to the entire app.
 * Wraps MSAL account info + get_user_profile API call into one clean context.
 *
 * Usage:
 *   const { callerEmail, userProfile, isLoading, profileStatus, profileErrorStatus,
 *           retryProfile } = useUser();
 *
 * profileStatus is the bootstrap outcome, surfaced so ONE place can render a
 * terminal panel instead of every guard having to infer it from a null profile:
 *   'anonymous'     — no MSAL account; no request is made at all
 *   'loading'       — request in flight
 *   'ready'         — 200, userProfile populated
 *   'unprovisioned' — 403 OR 404; the account resolved to no active Users doc
 *   'authError'     — 401 or any other outcome, including a network failure
 *
 * 403 and 404 both mean "no active Users doc for this caller" — they differ only
 * by which backend generation answered. The self-only endpoint returns 403 (the
 * air gap denies a null principal); the older parameter-based one returned 404.
 * Collapsing them is what makes the mapping deploy-order-independent.
 *
 * profileErrorStatus carries the raw HTTP status for display, so the terminal
 * panel can quote a reference the operator can act on. null unless the fetch
 * produced a non-200; 0 when the request never got a status at all (network
 * failure, CORS rejection, abort).
 *
 * The provider renders children UNCONDITIONALLY. It must stay that way: it
 * mounts above AppContent's public /legal/* routes, which have to render for a
 * visitor with no account at all.
 */

import { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import { apiFetch } from '../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

const UserContext = createContext({
  callerEmail: null,
  userProfile: null,
  isLoading: true,
  profileStatus: 'loading',
  profileErrorStatus: null,
  retryProfile: () => {},
});

export function UserProvider({ children }) {
  const { accounts } = useMsal();
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileStatus, setProfileStatus] = useState('loading');
  const [profileErrorStatus, setProfileErrorStatus] = useState(null);
  const [attempt, setAttempt] = useState(0);

  // MSAL's username IS what Entra puts in preferred_username — the user's real
  // email address, guests included. Send it as-is (lowercased); never derive.
  const callerEmail = accounts[0]?.username?.toLowerCase() ?? null;

  // Re-runs the effect by bumping its dependency. Exposed so the terminal
  // authError panel can retry in place rather than redirecting.
  const retryProfile = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!callerEmail) {
      setUserProfile(null);
      setProfileStatus('anonymous');
      setProfileErrorStatus(null);
      setIsLoading(false);
      return undefined;
    }

    let alive = true;
    setIsLoading(true);
    setProfileStatus('loading');
    setProfileErrorStatus(null);

    // ?email= is retained deliberately. The backend now ignores it (the endpoint
    // is self-only and reads identity from the token), but keeping it means a
    // stale SWA still sends what an older Function App would have needed.
    //
    // Deploy order is genuinely independent now, which it was NOT before: the
    // two backend generations disagree on the no-active-doc status — self-only
    // answers 403, the parameter-based one answered 404 — and this mapping used
    // to send 404 to 'authError'. An SWA that landed first showed a Retry panel
    // for a deactivated account. Collapsing 403 and 404 onto 'unprovisioned' is
    // what removes the ordering constraint; do not split them again.
    apiFetch(`${API_BASE_URL}/get_user_profile?email=${encodeURIComponent(callerEmail)}`)
      .then(async (res) => {
        if (!alive) return;
        if (res.status === 200) {
          const profile = await res.json();
          if (!alive) return;
          setUserProfile(profile);
          setProfileStatus('ready');
          setProfileErrorStatus(null);
          return;
        }
        setUserProfile(null);
        setProfileErrorStatus(res.status);
        setProfileStatus(
          res.status === 403 || res.status === 404 ? 'unprovisioned' : 'authError'
        );
        console.warn('UserContext: profile fetch returned', res.status, 'for', callerEmail);
      })
      .catch((err) => {
        if (!alive) return;
        setUserProfile(null);
        // No response reached us, so there is no status to quote. 0 is the
        // conventional "never got a status" sentinel and keeps the panel's
        // reference line from rendering the string "null".
        setProfileErrorStatus(0);
        setProfileStatus('authError');
        console.warn('UserContext: could not load profile for', callerEmail, err.message);
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [callerEmail, attempt]);

  return (
    <UserContext.Provider
      value={{
        callerEmail,
        userProfile,
        isLoading,
        profileStatus,
        profileErrorStatus,
        retryProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
